import { useEffect, useState, type ChangeEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Eye,
  FileText,
  Plus,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@qargo-ims/ui';
import { apiFetch } from '@/lib/api/client';
import { useApi } from '@/lib/api/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatShortDate } from '@/lib/format';
import { US_STATES } from '@/lib/usStates';
import {
  deleteLocationFile,
  listLocationFiles,
  previewUrl,
  sharePointConfigured,
  uploadLocationFile,
  type DriveFile,
} from '@/lib/sharepoint';

type DetailTab = 'details' | 'custom' | 'documents';

interface Attribute {
  id: string;
  label: string;
  value: string;
  sortOrder: number;
}
interface StoreDetail {
  id: string;
  code: string;
  name: string;
  address: string | null;
  state: string | null;
  isKiosk: boolean;
  phone: string | null;
  email: string | null;
  teamsWebhookUrl: string | null;
  notes: string | null;
  createdAt: string;
  baristaCount: number;
  attributes: Attribute[];
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function AdminLocationDetail() {
  const { id = '' } = useParams();
  const { data, loading, error, refetch } = useApi<{ store: StoreDetail }>(
    id ? `/stores/${id}` : null,
  );
  const store = data?.store;
  const [tab, setTab] = useState<DetailTab>('details');

  if (loading || !store) {
    return (
      <div className="space-y-6">
        <Link
          to="/admin/locations"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden /> Locations
        </Link>
        {error ? (
          <p className="text-sm text-destructive">Could not load location: {error}</p>
        ) : (
          <Skeleton className="h-64 w-full rounded-xl" />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/locations"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden /> Locations
      </Link>
      <PageHeader
        title={store.name}
        description={`${store.code}${store.state ? ` · ${store.state}` : ''}${store.isKiosk ? ' · Kiosk' : ''} · ${store.baristaCount} barista${store.baristaCount === 1 ? '' : 's'}`}
        actions={
          <Link
            to="/admin/locations"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Users className="size-4" aria-hidden /> Manage roster
          </Link>
        }
      />

      <div className="flex gap-1 border-b border-border">
        {(
          [
            ['details', 'Details'],
            ['custom', 'Custom info'],
            ['documents', 'Documents'],
          ] as [DetailTab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'details' ? <DetailsCard store={store} onSaved={refetch} /> : null}
      {tab === 'custom' ? <AttributesCard store={store} onSaved={refetch} /> : null}
      {tab === 'documents' ? <DocumentsTab folderName={store.code} /> : null}
    </div>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function DocumentsTab({ folderName }: { folderName: string }) {
  const [files, setFiles] = useState<DriveFile[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setFiles(await listLocationFiles(folderName));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (sharePointConfigured) void reload();
    else setLoading(false);
  }, [folderName]);

  async function onUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadLocationFile(folderName, file);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onPreview(f: DriveFile) {
    const url = await previewUrl(f.id);
    if (url) setPreview({ name: f.name, url });
    else window.open(f.webUrl, '_blank', 'noopener');
  }

  async function onDelete(f: DriveFile) {
    if (!window.confirm(`Delete "${f.name}"?`)) return;
    setBusy(true);
    try {
      await deleteLocationFile(f.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (!sharePointConfigured) {
    return (
      <EmptyState
        icon={FileText}
        title="Document storage not configured"
        description="VITE_SHAREPOINT_DRIVE_ID is not set for this build."
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Documents</CardTitle>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
            <Upload className="size-4" aria-hidden />
            {busy ? 'Working…' : 'Upload'}
            <input type="file" className="hidden" onChange={onUpload} disabled={busy} />
          </label>
        </div>
        <p className="text-sm text-muted-foreground">
          Stored in SharePoint under this location’s folder. Leases, permits, vendor contracts, etc.
        </p>
      </CardHeader>
      <CardContent>
        {error ? <p className="mb-3 text-sm text-destructive">{error}</p> : null}
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !files || files.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No documents yet"
            description="Upload a file to store it in this location’s SharePoint folder."
          />
        ) : (
          <ul className="divide-y divide-border">
            {files.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2.5">
                <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{f.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {fmtBytes(f.size)} · {formatShortDate(f.lastModifiedDateTime)}
                  </span>
                </span>
                <Button variant="ghost" size="sm" onClick={() => void onPreview(f)}>
                  <Eye className="size-4" aria-hidden /> Preview
                </Button>
                <a
                  href={f.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Open in SharePoint"
                >
                  <ExternalLink className="size-4" aria-hidden />
                </a>
                <button
                  type="button"
                  onClick={() => void onDelete(f)}
                  disabled={busy}
                  aria-label="Delete"
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                >
                  <Trash2 className="size-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {preview ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreview(null)}
        >
          <div
            className="flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-border bg-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <span className="truncate text-sm font-medium">{preview.name}</span>
              <button
                type="button"
                onClick={() => setPreview(null)}
                aria-label="Close"
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>
            <iframe
              title={preview.name}
              src={preview.url}
              className="h-full w-full bg-background"
            />
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function DetailsCard({ store, onSaved }: { store: StoreDetail; onSaved: () => void }) {
  const [form, setForm] = useState({
    code: store.code,
    name: store.name,
    address: store.address ?? '',
    state: store.state ?? '',
    isKiosk: store.isKiosk,
    phone: store.phone ?? '',
    email: store.email ?? '',
    teamsWebhookUrl: store.teamsWebhookUrl ?? '',
    notes: store.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function save() {
    // Double-check before removing kiosk status — it changes how the
    // location is treated operationally and is easy to untick by accident.
    if (store.isKiosk && !form.isKiosk) {
      const ok = window.confirm(
        `${store.code} — ${store.name} is currently marked as a kiosk.\n\n` +
          'Remove its kiosk designation? This changes how the location is ' +
          'classified across the app.',
      );
      if (!ok) return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const nul = (v: string) => (v.trim() ? v.trim() : null);
      await apiFetch(`/stores/${store.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          code: form.code,
          name: form.name,
          address: nul(form.address),
          state: nul(form.state),
          isKiosk: form.isKiosk,
          phone: nul(form.phone),
          email: nul(form.email),
          teamsWebhookUrl: nul(form.teamsWebhookUrl),
          notes: nul(form.notes),
        }),
      });
      setMsg('Saved');
      onSaved();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  function input(label: string, key: Exclude<keyof typeof form, 'isKiosk'>, max?: number) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <input
          className={inputClass}
          value={form[key]}
          maxLength={max}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {input('Name', 'name', 128)}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Location Code</label>
            <input
              className={inputClass}
              value={form.code}
              maxLength={4}
              pattern="[A-Za-z0-9]{1,4}"
              title="Up to 4 alphanumeric characters — the store identifier (also names the SharePoint folder)"
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">State</label>
            <select
              className={inputClass}
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
            >
              <option value="">—</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Kiosk</label>
            <label className="flex h-9 cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={form.isKiosk}
                onChange={(e) => setForm({ ...form, isKiosk: e.target.checked })}
              />
              This location is a kiosk
            </label>
          </div>
          {input('Address', 'address', 256)}
          {input('Phone', 'phone', 32)}
          {input('Email', 'email', 256)}
          <div className="space-y-1 sm:col-span-2 lg:col-span-3">
            <label className="text-xs font-medium text-muted-foreground">Teams webhook URL</label>
            <input
              className={inputClass}
              value={form.teamsWebhookUrl}
              maxLength={512}
              onChange={(e) => setForm({ ...form, teamsWebhookUrl: e.target.value })}
            />
          </div>
          <div className="space-y-1 sm:col-span-2 lg:col-span-3">
            <label className="text-xs font-medium text-muted-foreground">Notes</label>
            <textarea
              className={inputClass}
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save details'}
          </Button>
          {msg ? (
            <span className={msg === 'Saved' ? 'text-sm text-success' : 'text-sm text-destructive'}>
              {msg}
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function AttributesCard({ store, onSaved }: { store: StoreDetail; onSaved: () => void }) {
  const [rows, setRows] = useState<{ label: string; value: string }[]>(
    store.attributes.map((a) => ({ label: a.label, value: a.value })),
  );
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Keep local rows in sync if the store reloads with new attributes.
  useEffect(() => {
    setRows(store.attributes.map((a) => ({ label: a.label, value: a.value })));
  }, [store.attributes]);

  function update(i: number, key: 'label' | 'value', v: string) {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r)));
  }
  function add() {
    setRows([...rows, { label: '', value: '' }]);
  }
  function remove(i: number) {
    setRows(rows.filter((_, idx) => idx !== i));
  }

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const clean = rows
        .map((r) => ({ label: r.label.trim(), value: r.value.trim() }))
        .filter((r) => r.label.length > 0);
      await apiFetch(`/stores/${store.id}/attributes`, {
        method: 'PUT',
        body: JSON.stringify({ attributes: clean }),
      });
      setMsg('Saved');
      onSaved();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom information</CardTitle>
        <p className="text-sm text-muted-foreground">
          Utilities, rent, property management, lease dates — anything this location needs. Stored
          per location.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No custom fields yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <input
                  className={`${inputClass} sm:w-56`}
                  placeholder="Label (e.g. Monthly rent)"
                  value={r.label}
                  maxLength={128}
                  onChange={(e) => update(i, 'label', e.target.value)}
                />
                <input
                  className={inputClass}
                  placeholder="Value"
                  value={r.value}
                  onChange={(e) => update(i, 'value', e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label="Remove field"
                  className="mt-1 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                >
                  <Trash2 className="size-4" aria-hidden />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" onClick={add}>
            <Plus className="size-4" aria-hidden /> Add field
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save custom info'}
          </Button>
          {msg ? (
            <span className={msg === 'Saved' ? 'text-sm text-success' : 'text-sm text-destructive'}>
              {msg}
            </span>
          ) : (
            <Badge variant="muted">
              {rows.length} field{rows.length === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
